<script setup lang="ts">
  import { deckQueryFromSearchParams } from '@/lib/deckQuery'
  import SearchInput from '@/components/SearchInput.vue'
  import SelectTopic from './SelectTopic.vue'
  import { computed, toRefs } from 'vue'
  import { useClipboard, useShare, useToggle } from '@vueuse/core'
  import { useRouter } from 'vue-router'
  import { useNotify } from '@/composables/useNotify'
  import { storeToRefs } from 'pinia'
  import { useDeckStore } from '@/store'
  import { useUserStore } from '@/store/user'
  import { useI18n } from 'vue-i18n'
  import type { TopicLocale } from '@/types'
  import { PRODUCT_CLASSES } from '@/config/deck/classes'
  import { type SearchQueryParams } from 'afpnews-api'
  import { dequal } from 'dequal'
  import { preferredLocale } from '@/plugins/i18n'
  import { eventIdsFromQuery } from '@/lib/events'
  import EventNames from '@/components/EventNames.vue'
  import {
    AdjustmentsHorizontalIcon,
    CalendarIcon,
    GlobeEuropeAfricaIcon,
    ShareIcon,
    SparklesIcon,
    TrashIcon
  } from '@heroicons/vue/24/outline'

  const deckStore = useDeckStore()
  const { getColumnById, updateColumnParams, excludeAgenda } = deckStore
  const { aiEnabled } = storeToRefs(useUserStore())
  const { t } = useI18n()

  const props = defineProps({
    columnId: {
      type: String,
      required: true
    }
  })
  const { columnId } = toRefs(props)

  const emit = defineEmits<{
    (e: 'reset'): void
    (e: 'close'): void
    (e: 'toggle-agenda'): void
  }>()

  const [paramsOpen, toggleFilters] = useToggle(false)

  const column = computed(() => getColumnById(columnId.value))
  // Bornes par défaut d'une colonne (1980 → now) : inutiles pour le résumé et la carte
  const columnSearch = computed(() => ({
    ...column.value?.params,
    dateFrom: undefined,
    dateTo: undefined
  }))
  const router = useRouter()
  const notify = useNotify()
  const { copy } = useClipboard({ legacy: true })
  const { share, isSupported: shareSupported } = useShare()

  async function shareColumn() {
    const { href } = router.resolve({
      name: 'deck-new',
      query: deckQueryFromSearchParams(columnSearch.value)
    })
    const url = new URL(href, window.location.origin).href
    if (shareSupported.value) {
      await share({ url }).catch(() => {})
      return
    }
    await copy(url)
    notify.success(t('column.link-copied'))
  }

  function generateLabel(type: string, name: string) {
    return {
      label: t(`${type}.${name}`),
      value: name
    }
  }

  const classOptions = ['all', ...PRODUCT_CLASSES].map(name =>
    generateLabel('classes', name)
  )
  const langOptions = ['all', 'en', 'fr', 'de', 'es', 'pt', 'ar'].map(name =>
    generateLabel('languages', name)
  )

  // `class` peut être une string ou un objet `{ in?, exclude? }` ; hors string simple on retombe sur 'all' dans le select.
  const selectedClass = computed(() => {
    const cls = column.value?.params.class
    return typeof cls === 'string' ? cls : 'all'
  })
  const selectedLang = computed(() => {
    const langs = column.value?.params.langs
    return Array.isArray(langs) && langs.length > 0
      ? (langs[0] ?? 'all')
      : 'all'
  })
  const showLang = computed(() => selectedClass.value !== 'picture')

  const columnEventIds = computed(() =>
    eventIdsFromQuery(column.value?.params.query)
  )
  const eventLangs = computed(() => [selectedLang.value, preferredLocale.value])

  async function updateParams(newParams: SearchQueryParams) {
    if (!column.value) return
    if (newParams.class === 'picture') newParams['langs'] = []
    const merged = { ...column.value.params, ...newParams }
    // Sauvegarde sans changement : ne pas vider ni refetcher la colonne
    if (dequal(merged, column.value.params)) return
    await updateColumnParams(columnId.value, merged)
    emit('reset')
  }

  function onQueryChange(query: string) {
    updateParams({ query })
  }
</script>

<template>
  <form
    v-if="column"
    @submit.prevent=""
  >
    <p
      v-if="columnEventIds.length && column.type !== 'event'"
      class="truncate px-1 pt-1 text-sm font-semibold"
    >
      <EventNames
        :event-ids="columnEventIds"
        :langs="eventLangs"
      />
    </p>
    <div class="join mb-2 w-full pt-1">
      <SelectTopic
        v-if="column.type === 'topic' && typeof column.params.topic == 'string'"
        :modelValue="column.params.topic"
        @update:modelValue="($event: string) => updateParams({ topic: $event })"
        :lang-selected="selectedLang as TopicLocale"
      />
      <div
        v-else-if="column.type === 'event'"
        class="input input-lg flex grow items-center gap-2 overflow-hidden border-0 focus:outline-0"
      >
        <CalendarIcon class="size-6 shrink-0 opacity-60" />
        <EventNames
          class="line-clamp-2 min-w-0 flex-1 text-xs leading-tight font-semibold break-words whitespace-normal"
          :event-ids="columnEventIds"
          :langs="eventLangs"
        />
      </div>
      <SearchInput
        v-else-if="column.type === 'search'"
        :initial-query="column.params.query"
        :type="
          column.params.class && typeof column.params.class == 'string'
            ? column.params.class
            : 'text'
        "
        class="input input-lg border-0 font-semibold focus:outline-0"
        @submit="onQueryChange"
      />

      <button
        data-drag-handle
        :aria-label="t('column.filters')"
        :title="t('column.filters')"
        class="btn btn-lg bg-base-100 cursor-grab border-0 text-center outline-0 active:cursor-grabbing"
        @click="toggleFilters()"
      >
        <!-- Filters icon / drag handle -->
        <AdjustmentsHorizontalIcon class="size-6" />
      </button>
    </div>

    <TransitionGroup
      name="curtain"
      tabindex="-1"
      tag="ul"
      class="list"
    >
      <li
        v-if="paramsOpen && column.type !== 'topic'"
        class="list-row p-2"
        key="classes"
      >
        <div class="list-col-grow">
          <select
            key="classe"
            :value="selectedClass"
            @change="
              ev =>
                (ev.target as HTMLSelectElement).value !== 'all'
                  ? updateParams({
                      class: (ev.target as HTMLSelectElement).value
                    })
                  : updateParams({ class: { exclude: ['picture'] } })
            "
            name="classe"
            aria-label="Select a product"
            class="select"
          >
            <option
              v-for="{ label, value } in classOptions"
              :key="value"
              :value="value"
            >
              {{ label }}
            </option>
          </select>
        </div>
      </li>

      <li
        v-if="paramsOpen && showLang"
        key="languages"
        class="list-row p-2"
      >
        <div class="list-col-grow">
          <select
            :value="selectedLang"
            @change="
              ev =>
                (ev.target as HTMLSelectElement).value !== 'all'
                  ? updateParams({
                      langs: [(ev.target as HTMLSelectElement).value]
                    })
                  : updateParams({ langs: undefined })
            "
            name="languages"
            aria-label="Select a language"
            class="select"
          >
            <option
              v-for="{ label, value } in langOptions"
              :key="value"
              :value="value"
            >
              {{ label }}
            </option>
          </select>
        </div>
      </li>

      <li
        v-if="paramsOpen && column.type == 'search'"
        key="providers"
        class="list-row p-2"
      >
        <div class="list-col-grow">
          <select
            :value="
              Array.isArray(column.params.provider)
                ? 'afp-only'
                : column.params.provider &&
                    typeof column.params.provider == 'object' &&
                    'exclude' in column.params.provider &&
                    column.params.provider.exclude
                  ? 'others-providers'
                  : null
            "
            @change="
              event => {
                if (
                  !event.target ||
                  'value' in event.target === false ||
                  !event.target.value
                )
                  return
                if (event.target.value === 'afp-only') {
                  updateParams({ provider: ['afp', 'أ ف ب'] })
                } else if (event.target.value == 'others-providers') {
                  updateParams({ provider: { exclude: ['afp', 'أ ف ب'] } })
                }
              }
            "
            class="select"
          >
            <option value="afp-only">
              {{ t('providers.afp-only') }}
            </option>
            <option value="others-providers">
              {{ t('providers.others-providers') }}
            </option>
          </select>
        </div>
      </li>

      <li
        v-if="paramsOpen"
        key="other-settings"
        class="list-row p-2"
      >
        <div class="list-col-grow join ml-auto">
          <RouterLink
            v-if="aiEnabled"
            class="btn"
            :to="{
              name: 'ai-new',
              query: {
                'prompt-id': 'digest',
                ...deckQueryFromSearchParams(columnSearch)
              }
            }"
            :title="t('column.digest')"
          >
            <SparklesIcon class="size-6" />
          </RouterLink>
          <RouterLink
            class="btn"
            :to="{
              name: 'map',
              query: deckQueryFromSearchParams(columnSearch)
            }"
            :title="t('map.open')"
            :aria-label="t('map.open')"
          >
            <GlobeEuropeAfricaIcon class="size-6" />
          </RouterLink>
          <button
            type="button"
            class="btn"
            :title="t('column.share')"
            :aria-label="t('column.share')"
            @click="shareColumn"
          >
            <ShareIcon class="size-6" />
          </button>
          <button
            name="agenda"
            class="btn"
            title="Toggle agenda"
            :class="{
              'btn-success': !excludeAgenda(columnId)
            }"
            aria-label="Toggle agenda"
            @click="$emit('toggle-agenda')"
          >
            <CalendarIcon class="size-6" />
          </button>

          <button
            name="close"
            class="btn"
            title="Delete column"
            aria-label="Delete column"
            @click="$emit('close')"
          >
            <TrashIcon class="size-6" />
          </button>
        </div>
      </li>
    </TransitionGroup>
  </form>
</template>

<style lang="css" scoped>
  .curtain-enter-from {
    opacity: 0;
    transform: translateY(-30px);
  }
  .curtain-enter-active {
    transition:
      opacity 0.5s,
      transform 0.5s;
  }
  .curtain-enter-to {
    opacity: 1;
    transform: translateY(0px);
  }
</style>
